import React, { useState } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import DateInput from './DateInput';
import InfoTooltip from './InfoTooltip';

const REASONS = [
  { value: 'sabbatical', label: 'Sabbatical' },
  { value: 'compassionate', label: 'Compassionate leave' },
  { value: 'career_break', label: 'Career break' },
  { value: 'unpaid_holiday', label: 'Unpaid holiday' },
  { value: 'other', label: 'Other' },
];

export default function UnpaidLeaveModal({ employee, onSave, onClose }) {
  useEscapeKey(onClose);
  const [form, setForm] = useState({
    start_date: '',
    end_date: '',
    reason: 'sabbatical',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError('');
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.start_date) {
      setError('Start date is required');
      return;
    }
    if (!form.end_date) {
      setError('End date is required');
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      setError('End date cannot be before start date');
      return;
    }
    setSaving(true);
    try {
      await onSave(employee.id, {
        ...form,
        reason_label: REASONS.find(r => r.value === form.reason)?.label || form.reason,
      });
      onClose();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Unpaid Leave - {employee.first_name} {employee.last_name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Reason
                <InfoTooltip text="Why is the employee taking unpaid leave? This is recorded for your records — it doesn't change how pay is reduced." />
              </label>
              <select name="reason" value={form.reason} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                {REASONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start date *</label>
                <DateInput name="start_date" value={form.start_date} onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">End date *</label>
                <DateInput name="end_date" value={form.end_date} onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
              <textarea name="notes" rows="3" value={form.notes} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Any additional details..." />
            </div>

            <div className="p-3 bg-orange-50 dark:bg-orange-900/30 rounded text-sm text-orange-700 dark:text-orange-300">
              Base pay will be reduced by the working days in the unpaid leave window. The reduction follows the pro-rata method selected at the top of the run sheet.
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Record Unpaid Leave'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
